import { useFieldArray, useForm } from 'react-hook-form';
import {
	IManualInputFiltersData,
	IManualInputFiltersOptions,
	IManualInputFiltersCompetitor
} from '../models/manualInputFilters';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { UserResponseModel } from '../../../models/users/UserResponseModel';
import { useSelector } from 'react-redux';
import { IState } from '../../../store/IState';
import {
	INotifierService,
	NotifierService
} from '../../../services/notifierService';
import { HttpService, IHttpService } from '../../../services/httpService';
import { DropdownOption } from '../../../models/common/DropdownOption';
import { useManualInputCookies } from './useManualInputCookies';
import { getCurrentQuarterAndYear } from '../utilities/getCurrentQuarterAndYear';

export const useManualInputFilters = (
	setCompetitorSelectActive: React.Dispatch<React.SetStateAction<boolean>>
) => {
	const httpService: IHttpService = useMemo(() => new HttpService(), []);
	const notifierService: INotifierService = useMemo(
		() => new NotifierService(),
		[]
	);

	const user: UserResponseModel = useSelector(
		(state: IState) => state.UserReducer.userDetails
	);

	const { cookies } = useManualInputCookies();

	const [filtersReady, setFiltersReady] = useState<boolean>(false);

	const { control: filtersOptionsControl, reset: resetFiltersOptions } =
		useForm<IManualInputFiltersOptions>({
			defaultValues: {
				divisionOptions: [],
				countryOptions: [],
				brandOptions: [],
				productCategoryOptions: [],
				competitorSelectOptions: []
			}
		});

	const {
		control: filtersDataControl,
		setValue: setFiltersData,
		watch: watchFiltersData
	} = useForm<IManualInputFiltersData>({
		defaultValues: {
			yearFilter: new Date(),
			quarterFilter: 0,
			divisionFilter: 0,
			countryFilter: 0,
			brandFilter: 0,
			selectedProductCategoriesFilter: [],
			selectedCompetitorsFilter: []
		}
	});

	const { replace } = useFieldArray({
		control: filtersDataControl,
		name: 'selectedCompetitorsFilter'
	});

	const [divisionFilter, countryFilter] = watchFiltersData([
		'divisionFilter',
		'countryFilter'
	]);

	const onSelectedCompetitorsFilterUpdate = (competitorsIds: number[]) => {
		const idsMapped = competitorsIds.map((i) => {
			return {
				competitorId: i
			} as IManualInputFiltersCompetitor;
		});

		replace(idsMapped);
	};

	const populateDivisionSelectedOptions = useCallback(
		(divisionOptions: DropdownOption[]) => {
			const divisionIdFromCookies = cookies.manualInputDivision;

			if (
				divisionOptions
					.map((d) => d.value)
					.includes(divisionIdFromCookies)
			) {
				setFiltersData('divisionFilter', divisionIdFromCookies);
			}
		},
		[setFiltersData, cookies]
	);

	const populateCountrySelectedOptions = useCallback(
		(countryOptions: DropdownOption[]) => {
			const countryIdFromCookies = cookies.manualInputCountry;

			if (
				countryOptions
					.map((d) => d.value)
					.includes(countryIdFromCookies)
			) {
				setFiltersData('countryFilter', countryIdFromCookies);
			}
		},
		[setFiltersData, cookies]
	);

	const populateBrandSelectedOptions = useCallback(
		(brandOptions: DropdownOption[]) => {
			const brandIdFromCookies = cookies.manualInputBrand;

			if (brandOptions.map((d) => d.value).includes(brandIdFromCookies)) {
				setFiltersData('brandFilter', brandIdFromCookies);
			}
		},
		[setFiltersData, cookies]
	);

	const populateProductCategorySelectedOptions = useCallback(
		(productCategoryOptions: DropdownOption[]) => {
			setFiltersData('selectedProductCategoriesFilter', []);

			const productCategoriesFromCookies = cookies.manualInputCategories;

			if (!Array.isArray(productCategoriesFromCookies)) {
				return;
			}

			const productCategoryOptionsIds = productCategoryOptions.map(
				(x) => x.value
			);

			const validIds = productCategoriesFromCookies.filter((id: number) =>
				productCategoryOptionsIds.includes(id)
			);

			if (validIds) {
				setFiltersData('selectedProductCategoriesFilter', validIds);
			}
		},
		[setFiltersData, cookies]
	);

	const populateCompetitorsSelectedOptions = useCallback(
		(competitorOptions: DropdownOption[]) => {
			const competitorsFromCookies = cookies.manualInputCompetitors;

			if (!Array.isArray(competitorsFromCookies)) {
				return;
			}

			const competitorOptionsIds = competitorOptions.map((x) => x.value);

			const validIds = competitorsFromCookies
				.filter((id: number) => competitorOptionsIds.includes(id))
				.map((x) => {
					return { competitorId: x } as IManualInputFiltersCompetitor;
				});

			if (validIds) {
				setFiltersData('selectedCompetitorsFilter', validIds);
			}
		},
		[setFiltersData, cookies]
	);

	const populateFilters = useCallback(
		(
			divisionOptions: DropdownOption[],
			countryOptions: DropdownOption[],
			brandOptions: DropdownOption[],
			productCategoryOptions: DropdownOption[],
			competitorOptions: DropdownOption[]
		) => {
			resetFiltersOptions({
				divisionOptions: divisionOptions,
				countryOptions: countryOptions,
				brandOptions: brandOptions,
				productCategoryOptions: productCategoryOptions,
				competitorSelectOptions: competitorOptions
			});

			const { currentQuarter, currentYear } = getCurrentQuarterAndYear();

			setFiltersData(
				'quarterFilter',
				cookies.manualInputQuarter !== undefined
					? parseInt(cookies.manualInputQuarter)
					: currentQuarter
			);

			setFiltersData(
				'yearFilter',
				cookies.manualInputYear !== undefined
					? new Date(cookies.manualInputYear)
					: currentYear
			);

			populateDivisionSelectedOptions(divisionOptions);
			populateCountrySelectedOptions(countryOptions);
			populateBrandSelectedOptions(brandOptions);
			populateProductCategorySelectedOptions(productCategoryOptions);
			populateCompetitorsSelectedOptions(competitorOptions);
		},
		[
			cookies,
			setFiltersData,
			resetFiltersOptions,
			populateDivisionSelectedOptions,
			populateCountrySelectedOptions,
			populateBrandSelectedOptions,
			populateProductCategorySelectedOptions,
			populateCompetitorsSelectedOptions
		]
	);

	const handleFetchFiltersOptions = useCallback(async () => {
		if (!user.id) {
			return;
		}

		const divisions = httpService.get<DropdownOption[]>(
			`divisions/dropdown/rights?userId=${user.id}`,
			undefined
		);

		const countries = httpService.get<DropdownOption[]>(
			`countries/dropdown/rights?userId=${user.id}`,
			undefined
		);

		const brands = httpService.get<DropdownOption[]>(
			`brands/dropdown/rights?userId=${user.id}&countryId=${countryFilter}`,
			undefined
		);

		const productCategories = httpService.get<DropdownOption[]>(
			`productDetails/activeCategories?divisionId=${divisionFilter}`,
			undefined
		);

		const competitors = httpService.get<DropdownOption[]>(
			`brands/dropdown/?competitors=true`,
			undefined
		);

		try {
			const [
				divisionOptions,
				countryOptions,
				brandOptions,
				productCategoryOptions,
				competitorOptions
			] = await Promise.all([
				divisions,
				countries,
				brands,
				productCategories,
				competitors
			]);

			populateFilters(
				divisionOptions,
				countryOptions,
				brandOptions,
				productCategoryOptions,
				competitorOptions
			);
		} catch (error) {
			notifierService.error();
		} finally {
			setFiltersReady(true);
			setCompetitorSelectActive(false);
		}
	}, [
		populateFilters,
		setCompetitorSelectActive,
		httpService,
		notifierService,
		countryFilter,
		divisionFilter,
		user
	]);

	useEffect(() => {
		handleFetchFiltersOptions();
	}, [
		handleFetchFiltersOptions,
		httpService,
		notifierService,
		countryFilter,
		divisionFilter,
		user
	]);

	return {
		filtersReady,
		filtersDataControl,
		filtersOptionsControl,
		setFiltersData,
		onSelectedCompetitorsFilterUpdate
	};
};
